import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { updateFavorites } from '../utils/updateFavorites';
import { fetchData } from '../utils/fetchData';
import { API_BASE_URL } from '../utils/apiBaseUrl';

export const GlobalContext = createContext();

const CACHE_KEYS = {
  bands: 'bands-cache-v1',
  musicians: 'musicians-cache-v1',
};

const CACHE_TTL = 5 * 60 * 1000;

function getCachedData(key) {
  const raw = sessionStorage.getItem(key);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);
    const isFresh = Date.now() - parsed.savedAt < CACHE_TTL;

    if (!isFresh) {
      sessionStorage.removeItem(key);
      return null;
    }

    return parsed.data;
  } catch (error) {
    sessionStorage.removeItem(key);
    return null;
  }
}

function saveCachedData(key, data) {
  sessionStorage.setItem(
    key,
    JSON.stringify({
      savedAt: Date.now(),
      data,
    }),
  );
}

export const GlobalProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userId, setUserId] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const [musicians, setMusicians] = useState([]);
  const [bands, setBands] = useState([]);
  const [isBandsLoading, setIsBandsLoading] = useState(false);
  const [isMusiciansLoading, setIsMusiciansLoading] = useState(false);
  const [bandsLoaded, setBandsLoaded] = useState(false);
  const [musiciansLoaded, setMusiciansLoaded] = useState(false);

  const [favoriteIds, setFavoriteIds] = useState([]);
  const [notification, setNotification] = useState({
    text: '',
    type: 'info',
    visible: false,
  });
  const notificationTimeoutRef = useRef(null);

  const clearNotification = useCallback(() => {
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
      notificationTimeoutRef.current = null;
    }

    setNotification(prev => ({ ...prev, visible: false }));
  }, []);

  const showNotification = useCallback(
    (text, type = 'info', duration = 2600) => {
      if (!text) {
        return;
      }

      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }

      setNotification({ text, type, visible: true });

      notificationTimeoutRef.current = setTimeout(() => {
        setNotification(prev => ({ ...prev, visible: false }));
        notificationTimeoutRef.current = null;
      }, duration);
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);

  // Check if the user is already logged in LOCAL STORAGE
  useEffect(() => {
    const storedLoggedIn = localStorage.getItem('isLoggedIn');
    const storedUserId = localStorage.getItem('userId');
    const storedEmail = localStorage.getItem('userEmail');
    if (storedLoggedIn === 'true' && storedUserId) {
      setIsLoggedIn(true);
      setUserId(storedUserId);
      setUserEmail(storedEmail);
    }
  }, []);

  // Favorite Ids
  useEffect(() => {
    if (userId) {
      fetch(`${API_BASE_URL}/users/favorites/${userId}`)
        .then(response => response.json())
        .then(result => {
          setFavoriteIds([...result.favorites]);
        });
    }
  }, [userId]);

  function handleFavoriteId(cardId) {
    if (favoriteIds.includes(cardId)) {
      setFavoriteIds(favoriteIds.filter(id => id !== cardId));
      showNotification('Removed from favorites.', 'info');
    } else {
      setFavoriteIds([...favoriteIds, cardId]);
      showNotification('Added to favorites.', 'success');
    }
  }

  // isLoggedIn and get user ID and LogOut
  const handleLoggedIn = (userId, userEmail) => {
    if (userId) {
      setIsLoggedIn(true);
      setUserId(userId);
      setUserEmail(userEmail);
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('userId', userId);
      localStorage.setItem('userEmail', userEmail);
      showNotification('Signed in successfully.', 'success');
    }
  };
  const handleLogOut = () => {
    updateFavorites(userId, favoriteIds);
    setUserId('');
    setUserEmail('');
    setIsLoggedIn(false);
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    showNotification('Signed out.', 'info');
  };

  const loadBands = useCallback(
    async (forceRefresh = false) => {
      if (bandsLoaded && !forceRefresh) {
        return;
      }

      setIsBandsLoading(true);

      try {
        if (!forceRefresh) {
          const cachedBands = getCachedData(CACHE_KEYS.bands);

          if (cachedBands) {
            setBands(cachedBands);
            setBandsLoaded(true);
            return;
          }
        }

        const data = await fetchData(`${API_BASE_URL}/bands`);
        setBands(data);
        saveCachedData(CACHE_KEYS.bands, data);
        setBandsLoaded(true);
      } catch (error) {
        console.error('Error loading bands:', error);
        setBandsLoaded(true);
      } finally {
        setIsBandsLoading(false);
      }
    },
    [bandsLoaded],
  );

  const loadMusicians = useCallback(
    async (forceRefresh = false) => {
      if (musiciansLoaded && !forceRefresh) {
        return;
      }

      setIsMusiciansLoading(true);

      try {
        if (!forceRefresh) {
          const cachedMusicians = getCachedData(CACHE_KEYS.musicians);

          if (cachedMusicians) {
            setMusicians(cachedMusicians);
            setMusiciansLoaded(true);
            return;
          }
        }

        const data = await fetchData(`${API_BASE_URL}/musicians`);
        setMusicians(data);
        saveCachedData(CACHE_KEYS.musicians, data);
        setMusiciansLoaded(true);
      } catch (error) {
        console.error('Error loading musicians:', error);
        setMusiciansLoaded(true);
      } finally {
        setIsMusiciansLoading(false);
      }
    },
    [musiciansLoaded],
  );

  return (
    <GlobalContext.Provider
      value={{
        musicians,
        bands,
        loadBands,
        loadMusicians,
        isBandsLoading,
        isMusiciansLoading,
        bandsLoaded,
        musiciansLoaded,
        handleLoggedIn,
        isLoggedIn,
        userId,
        userEmail,
        handleLogOut,
        favoriteIds,
        handleFavoriteId,
        notification,
        showNotification,
        clearNotification,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};
